/**
 * ExpertVideoUploadCard — Upload + gestion de la vidéo de présentation
 * affichée dans le carrousel CONFILEX MATCH™.
 *
 * - Durée recommandée : 30s à 2min
 * - Formats : MP4, WebM, MOV
 * - Taille max : 50 Mo
 * - Preview immédiat
 * - Bouton de suppression
 *
 * Pas de transcoding côté frontend — on suppose un fichier déjà optimisé
 * (les navigateurs modernes lisent nativement MP4 H.264 et WebM VP9).
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Video, Upload, Trash2, Loader2, CheckCircle2, AlertCircle, Play } from "lucide-react";
import { apiClient } from "@/lib/api";

const MAX_MB = 50;
const ACCEPT = "video/mp4,video/webm,video/quicktime,video/x-m4v";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

export default function ExpertVideoUploadCard() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const inputRef = useRef(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await apiClient.get("/experts/me");
      setProfile(r.data || null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Impossible de charger votre profil spécialiste.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const videoUrl = profile?.video_url || profile?.video;
  const absoluteVideoUrl = videoUrl
    ? (videoUrl.startsWith("http") ? videoUrl : `${BACKEND}${videoUrl}`)
    : null;

  const onPick = () => inputRef.current?.click();

  const onChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setError(null);
    setSuccess(null);

    // Garde-fou taille / type
    if (file.size > MAX_MB * 1024 * 1024) {
      setError(`Fichier trop volumineux (${(file.size / 1024 / 1024).toFixed(1)} Mo). Max ${MAX_MB} Mo.`);
      return;
    }
    if (!file.type.startsWith("video/")) {
      setError("Le fichier doit être une vidéo (MP4, WebM, MOV).");
      return;
    }

    setUploading(true);
    setProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      // Utilise l'instance axios avec credentials (cookie ou Bearer auto)
      const url = `${BACKEND}/api/experts/me/video`;
      // Recopie de la logique d'auth (Bearer si présent en localStorage, sinon cookie)
      const token = localStorage.getItem("confilex_token");
      await axios.post(url, fd, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      setSuccess("Vidéo publiée. Elle apparaît désormais dans le carrousel CONFILEX MATCH™.");
      await refresh();
    } catch (e) {
      setError(e?.response?.data?.detail || "Échec de l'upload. Réessayez.");
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const onDelete = async () => {
    if (!window.confirm("Supprimer définitivement votre vidéo de présentation ?")) return;
    try {
      await apiClient.delete("/experts/me/video");
      setSuccess("Vidéo supprimée.");
      await refresh();
    } catch (e) {
      setError(e?.response?.data?.detail || "Suppression impossible.");
    }
  };

  return (
    <Card className="p-6 rounded-2xl border-slate-100" data-testid="expert-video-card">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#C9A961] mb-1.5 inline-flex items-center gap-1.5">
            <Video className="w-3.5 h-3.5" /> Présentation vidéo
          </div>
          <h2 className="font-serif text-2xl text-[#0B3A2A]">Vidéo de présentation</h2>
          <p className="text-sm text-slate-500 mt-1 max-w-xl">
            Une courte présentation (30s à 2min) qui apparaît dans le carrousel
            CONFILEX MATCH™ aux utilisateurs orientés vers votre profil.
            <b className="text-slate-700"> Facultatif</b> · MP4/WebM/MOV · Max {MAX_MB} Mo.
          </p>
        </div>
        {videoUrl && !uploading && (
          <Button
            variant="outline"
            onClick={onDelete}
            className="rounded-full border-rose-200 text-rose-700 hover:bg-rose-50"
            data-testid="expert-video-delete"
          >
            <Trash2 className="w-4 h-4 mr-2" /> Supprimer
          </Button>
        )}
      </div>

      {loading ? (
        <Loader2 className="w-5 h-5 animate-spin text-[#C9A961]" />
      ) : !profile ? (
        <Card className="p-4 bg-amber-50 border-amber-200 text-sm text-amber-900">
          Vous devez d'abord compléter votre profil spécialiste pour ajouter une vidéo.
        </Card>
      ) : (
        <>
          {videoUrl ? (
            <div className="grid md:grid-cols-2 gap-4 items-start" data-testid="expert-video-preview">
              <video
                src={absoluteVideoUrl}
                controls
                preload="metadata"
                className="w-full rounded-xl bg-black aspect-video"
                data-testid="expert-video-player"
              />
              <div className="space-y-2">
                <p className="text-sm text-slate-700 inline-flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  Vidéo publiée
                </p>
                {profile?.video_uploaded_at && (
                  <p className="text-xs text-slate-500">
                    Mise en ligne : {new Date(profile.video_uploaded_at).toLocaleString("fr-FR")}
                  </p>
                )}
                {profile?.video_size_bytes && (
                  <p className="text-xs text-slate-500">
                    Taille : {(profile.video_size_bytes / 1024 / 1024).toFixed(1)} Mo
                  </p>
                )}
                <Button
                  onClick={onPick}
                  variant="outline"
                  className="mt-2 rounded-full"
                  disabled={uploading}
                  data-testid="expert-video-replace"
                >
                  <Upload className="w-4 h-4 mr-2" /> Remplacer la vidéo
                </Button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={onPick}
              disabled={uploading}
              data-testid="expert-video-upload-btn"
              className="w-full p-10 border-2 border-dashed border-slate-200 hover:border-[#C9A961] rounded-2xl text-center transition disabled:opacity-50"
            >
              <Play className="w-10 h-10 mx-auto text-[#C9A961] mb-2" />
              <p className="text-base text-[#0B3A2A] font-medium">Ajouter une vidéo de présentation</p>
              <p className="text-xs text-slate-500 mt-1">
                Glissez-déposez ou cliquez pour sélectionner · MP4/WebM/MOV · Max {MAX_MB} Mo
              </p>
              <p className="text-[11px] text-slate-400 mt-2 italic">
                Conseil : tournez en 1080p horizontal, éclairage neutre, 30s à 2min.
              </p>
            </button>
          )}

          {uploading && (
            <div className="mt-4" data-testid="expert-video-uploading">
              <div className="flex items-center gap-2 text-sm text-slate-700 mb-2">
                <Loader2 className="w-4 h-4 animate-spin text-[#C9A961]" />
                Upload en cours… <b>{progress}%</b>
              </div>
              <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                <div className="h-full bg-[#C9A961] transition-all" style={{ width: `${progress}%` }} />
              </div>
            </div>
          )}

          {error && (
            <p className="mt-3 text-sm text-rose-700 flex items-start gap-2" data-testid="expert-video-error">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> {error}
            </p>
          )}
          {success && !error && (
            <p className="mt-3 text-sm text-emerald-700 flex items-start gap-2" data-testid="expert-video-success">
              <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" /> {success}
            </p>
          )}

          <p className="text-[11px] text-slate-500 italic mt-5 leading-relaxed">
            Le contenu doit rester factuel et conforme aux exigences déontologiques de votre
            profession. Aucune promesse de résultat, aucun taux de succès ni comparatif n'est
            autorisé. Confilex se réserve le droit de masquer une vidéo non conforme.
          </p>
        </>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        className="hidden"
        onChange={onChange}
        data-testid="expert-video-input"
      />
    </Card>
  );
}
